import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  submitEmergencyContact,
  selectContactById,
  selectSubmitLoading
} from '../../store/emergencyContact/emergencyContactSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const EmergencyContactFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
  const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const existingContact = useSelector((state) => selectContactById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    // submitLoading (Redux) only reflects the self-contained dispatch branch
    // below - it stays false the whole time when onSubmit is used instead
    // (the parent's onSubmit does the dispatching), which would otherwise
    // leave the Submit button clickable during that async work. Mirrors the
    // same local guard already used in LunchPlanWaiverFormComp.js /
    // NoPeanutPolicyFormComp.js.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
      contact_name: '',
      relation: '',
      phone: ''
    });
  
    useEffect(() => {
      if (isEditMode && existingContact) {
        setFormData({
          contact_name: existingContact.contact_name || '',
          relation: existingContact.relation || '',
          // Re-format in case this contact was saved under the old
          // "+1-XXX-XXX-XXXX" scheme, so editing it doesn't keep
          // re-submitting a leading "+" (see formatPhoneNumber below).
          phone: formatPhoneNumber(existingContact.phone || '')
        });
      }
    }, [isEditMode, existingContact]);

    // Stored/submitted as plain digits (no "+" or country code) — the API
    // rejects a "+" in this field. This used to format as "+1-XXX-XXX-XXXX",
    // which made every submission fail; matches the app's
    // EmergencyContactFormScreen.js, which hit and fixed this same bug.
    const formatPhoneNumber = (value) => {
      const phoneNumber = value.replace(/\D/g, '').slice(0, 10);
      if (phoneNumber.length <= 3) return phoneNumber;
      if (phoneNumber.length <= 6) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };
  
    const handleInputChange = (field, value) => {
      if (field === 'phone') {
        const formattedPhone = formatPhoneNumber(value);
        setFormData(prev => ({
          ...prev,
          [field]: formattedPhone
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();

      if (isSubmitting) {
        return;
      }

      // Validation
      if (!formData.contact_name.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide contact name.'
        });
        return;
      }
  
      if (!formData.relation.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide relation.'
        });
        return;
      }
  
      if (!formData.phone.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide phone number.'
        });
        return;
      }
  
      // Validate phone has all 10 digits (US/Canada format, no country code)
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Phone',
          text: 'Please provide a complete phone number (10 digits).'
        });
        return;
      }
  
      setIsSubmitting(true);

      try {
        const submitData = { ...formData };

        if (isEditMode) {
          submitData.id = id;
        }

        submitData.student_id = getEffectiveStudentId(profileData);

        // Same reasoning as LunchPlanWaiverFormComp.js / NoPeanutPolicyFormComp.js:
        // when embedded in the profile page's Registration Packet tab, let the
        // host page's onSubmit own dispatch + success feedback + in-place
        // refresh, instead of also self-dispatching and navigating away to
        // /dashboard/emergency-contact/list. Standalone add/edit routes never
        // pass onSubmit, so they're unaffected.
        if (onSubmit) {
          await onSubmit(submitData);
        } else {
          await dispatch(submitEmergencyContact(submitData)).unwrap();

          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `Emergency contact ${isEditMode ? 'updated' : 'added'} successfully!`
          });

          navigate('/dashboard/emergency-contact/list');
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error || 'Failed to submit contact'
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Container>
        <Row>
          <Col sm="12">
            <Card>
              {/* Only rendered when a remarks prop is actually passed in -
                  same convention AllergyActionPlanFormComp.js /
                  LunchPlanWaiverFormComp.js / NoPeanutPolicyFormComp.js
                  already use. This component previously always rendered
                  its own header regardless, which duplicated the title the
                  standalone add/edit route's own page header (in
                  emergency-contact-form.js) already shows above this card. */}
              {remarks &&
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div className="header-title">
                    <h4 className="card-title">
                      {isEditMode ? 'Edit' : 'Add'} {remarks}
                    </h4>
                  </div>
                </Card.Header>
              }
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <div className="mb-2">
                    <div className="d-flex align-items-center mb-4">
                      <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                        <i className="material-symbols-outlined">person</i>
                      </div>
                      <h5 className="mb-0 fw-bold text-dark">Contact Information</h5>
                    </div>
                    <Row className="mb-3">
                      <Col md="6">
                        <Form.Group className="mb-3">
                          <Form.Label>Contact Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., John Doe"
                            value={formData.contact_name}
                            onChange={(e) => handleInputChange('contact_name', e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group className="mb-3">
                          <Form.Label>Relation <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Father, Mother, Grandmother, etc."
                            value={formData.relation}
                            onChange={(e) => handleInputChange('relation', e.target.value)}
                            required
                          />
                          <Form.Text className="text-muted">
                            Relationship to child (e.g., Father, Mother, Aunt, Uncle, Grandparent)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md="6">
                        <Form.Group className="mb-3">
                          <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="Start typing phone number..."
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            required
                            maxLength={12}
                          />
                          <Form.Text className="text-muted">
                            Format: XXX-XXX-XXXX (US/Canada)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="mb-4 p-3 d-flex align-items-start gap-2" style={{ background: '#e7f3ff', border: '1px solid #b6d9f7', borderRadius: '10px' }}>
                      <i className="material-symbols-outlined text-primary flex-shrink-0" style={{ fontSize: '20px' }}>info</i>
                      <p className="mb-0" style={{ fontSize: '14px' }}>
                        <strong>Note:</strong> Emergency contacts will be called in case of an emergency when primary guardians cannot be reached.
                      </p>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="d-flex justify-content-end gap-2 pt-3 mt-2" style={{ borderTop: '1px solid #eef0f3' }}>
                    {/* <Button
                      variant="secondary"
                      onClick={() => navigate('/dashboard/emergency-contact/list')}
                      disabled={submitLoading}
                    >
                      Cancel
                    </Button> */}
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting || submitLoading}
                    >
                      {isSubmitting || submitLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>check_circle</i>
                          {isEditMode ? 'Update' : 'Add'} Contact
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
};
export default EmergencyContactFormComp;
